/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { createContext, type ForwardedRef, forwardRef } from 'react';
import {
  type ContextValue,
  Text as TextCore,
  type TextProps as TextCoreProps,
  useContextProps,
} from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { splitByQuery } from '../../utils/split-by-query/split-by-query.util';

interface ParagraphProps extends Omit<TextCoreProps, 'style' | 'className' | 'elementType'> {
  /** The size variant of the paragraph. (Default: 16) */
  size?: keyof typeof sizeVariants;
  /** The color variant of the paragraph. (Default: primary) */
  color?: keyof typeof colorVariants;
  /** The weight variant of the paragraph. (Default: regular) */
  weight?: keyof typeof weightVariants;
  /** Highlight all occurrences of this string within children (case-insensitive). Only works when children is a plain string. */
  query?: string;
  /** Background color of the highlight mark. Matches the Message component variant. (Default: received) */
  highlightColor?: keyof typeof highlightColorVariants;
  style?: stylex.StyleXStyles;
}

const ParagraphContext = createContext<ContextValue<ParagraphProps, HTMLParagraphElement>>({});

const Paragraph = forwardRef((props: ParagraphProps, ref: ForwardedRef<HTMLParagraphElement>) => {
  [props, ref] = useContextProps(props, ref, ParagraphContext);

  const {
    size = 16,
    color = 'primary',
    weight = 'regular',
    query,
    highlightColor = 'yellowLight',
    style,
    children,
    ...coreProps
  } = props;

  const shouldHighlight = query && typeof children === 'string';
  const segments = shouldHighlight ? splitByQuery(children, query) : null;

  return (
    <TextCore
      elementType="p"
      ref={ref}
      {...stylex.props(
        styles.base,
        sizeVariants[size],
        colorVariants[color],
        weightVariants[weight],
        letterSpacingVariants[`${weight}${size.toString()}` as keyof typeof letterSpacingVariants],
        style,
      )}
      {...coreProps}>
      {segments
        ? segments.map((segment, index) => segment.isMatch
          ? <mark key={index} {...stylex.props(markStyles.base, highlightColorVariants[highlightColor])}>{segment.text}</mark>
          : segment.text)
        : children}
    </TextCore>
  );
});

const styles = stylex.create({
  base: {
    margin: 0,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontStyle: 'normal',
  },
});

const sizeVariants = stylex.create({
  20: {
    fontSize: 20,
    lineHeight: '34px',
  },
  18: {
    fontSize: 18,
    lineHeight: '32px',
  },
  16: {
    fontSize: 16,
    lineHeight: '28px',
  },
  14: {
    fontSize: 14,
    lineHeight: '26px',
  },
  12: {
    fontSize: 12,
    lineHeight: '24px',
  },
});

const colorVariants = stylex.create({
  primary: { color: $.textPrimary },
  secondary: { color: $.textSecondary },
  tertiary: { color: $.textTertiary },
  quaternary: { color: $.textQuaternary },
  placeholder: { color: $.textPlaceholder },
  disabled: { color: $.textDisabled },
  inverted: { color: $.textInverted },
  interactive: { color: $.textInteractive },
  interactiveHover: { color: $.textInteractiveHover },
  positive: { color: $.textPositive },
  positiveHover: { color: $.textPositiveHover },
  critical: { color: $.textCritical },
  criticalHover: { color: $.textCriticalHover },
  white: { color: $.textWhite },
  black: { color: $.textBlack },
});

const weightVariants = stylex.create({
  semibold: { fontWeight: 600 },
  medium: { fontWeight: 500 },
  regular: { fontWeight: 400 },
});

const letterSpacingVariants = stylex.create({
  semibold20: { letterSpacing: -0.2 },
  medium20: { letterSpacing: -0.2 },
  regular20: { letterSpacing: -0.2 },
  semibold18: { letterSpacing: -0.2 },
  medium18: { letterSpacing: -0.2 },
  regular18: { letterSpacing: -0.2 },
  semibold16: { letterSpacing: -0.15 },
  medium16: { letterSpacing: -0.15 },
  regular16: { letterSpacing: -0.15 },
  semibold14: { letterSpacing: -0.15 },
  medium14: { letterSpacing: -0.15 },
  regular14: { letterSpacing: -0.15 },
  semibold12: { letterSpacing: -0.1 },
  medium12: { letterSpacing: -0.1 },
  regular12: { letterSpacing: -0.1 },
});

const markStyles = stylex.create({
  base: {
    borderRadius: 4,
    paddingBlock: 0,
    paddingInline: 2,
    color: 'inherit',
    display: 'inline',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    letterSpacing: 'inherit',
    lineHeight: 'inherit',
  },
});

const highlightColorVariants = stylex.create({
  yellowLight: {
    backgroundColor: $.surfaceAttentiveMedium,
  },
  yellow: {
    backgroundColor: $.surfaceAttentive,
  },
});

export { Paragraph, ParagraphContext, type ParagraphProps };
